import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Mail, Phone, Users, Briefcase, MapPin, User, Calendar, Clock,
  CheckCircle, BookOpen, Target, Download, Shield, Key, Settings, Info, Search, X
} from 'lucide-react';
import useUsersStore from '@/stores/AdminStore/UsersStore';
import {
  getUserLearningHistory,
  LearningHistoryItem,
  getCoursesForEnrollment,
  EnrollmentCourse,
  enrollUserInCourse,
  unenrollUserFromCourse,
  changeUserStatus,
  UserStatus,
  changeUserRole,
  resetUserPassword,
  getAdminLogs,
  AdminLog,
} from '@/services/PagesServices/UsersService';
import { LoadingSpinner, ConfirmationModal, MessageModal, ErrorDisplay } from '@/Components/common';
import './UserDetailsPage.css';

const UserDetailsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { selectedUser, fetchUserById, loadingUsers } = useUsersStore();
  const [activeTab, setActiveTab] = useState<'details' | 'history' | 'enrollment' | 'admin'>('details');
  const [learningHistory, setLearningHistory] = useState<LearningHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Enrollment tab state
  const [enrollmentCourses, setEnrollmentCourses] = useState<EnrollmentCourse[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [coursesPage, setCoursesPage] = useState(1);
  const [coursesPerPage] = useState(10);
  const [coursesTotalPages, setCoursesTotalPages] = useState(1);
  const [enrollingCourseId, setEnrollingCourseId] = useState<number | null>(null);
  const [coursesError, setCoursesError] = useState<string | null>(null);

  // Admin logs state
  const [adminLogs, setAdminLogs] = useState<AdminLog[]>([]);
  const [loadingAdminLogs, setLoadingAdminLogs] = useState(false);
  const [adminLogsPage, setAdminLogsPage] = useState(1);
  const [adminLogsPerPage] = useState(10);
  const [adminLogsTotalPages, setAdminLogsTotalPages] = useState(1);

  // Modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<{
    type: 'enroll' | 'unenroll';
    courseId: number;
    courseName: string;
  } | null>(null);
  const [successMessage, setSuccessMessage] = useState({ title: '', message: '' });
  const [isProcessing, setIsProcessing] = useState(false);

  // Status change modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<UserStatus>('Active');
  const [isChangingStatus, setIsChangingStatus] = useState(false);

  // Role change modal states
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>('student');
  const [isChangingRole, setIsChangingRole] = useState(false);

  // Password reset modal states
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState<string>('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // Format date to dd/mm/yyyy HH:MM
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '--';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '--';

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      return '--';
    }
  };

  // Format field value, replacing empty/null with "--"
  const formatFieldValue = (value: string | null | undefined): string => {
    if (!value || value === 'N/A' || value.trim() === '') return '--';
    return value;
  };

  useEffect(() => {
    if (userId) {
      fetchUserById(userId);
      fetchLearningHistory();
    }
  }, [userId, fetchUserById]);

  const fetchLearningHistory = async () => {
    if (!userId) return;

    setLoadingHistory(true);
    try {
      const response = await getUserLearningHistory(userId);
      if (response.success && response.data) {
        setLearningHistory(response.data);
      }
    } catch (error) {
      console.error('Error fetching learning history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchCourses = async () => {
    if (!userId) return;

    setLoadingCourses(true);
    setCoursesError(null);
    try {
      const response = await getCoursesForEnrollment(userId, {
        search: courseSearchTerm,
        page: coursesPage,
        limit: coursesPerPage,
      });

      if (response.success && response.data) {
        setEnrollmentCourses(response.data);
        setCoursesTotalPages(response.pagination?.totalPages || 1);
      } else {
        setCoursesError(response.error?.message || 'Failed to load courses');
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCoursesError('Failed to load courses');
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchAdminLogs = async () => {
    if (!userId) return;

    setLoadingAdminLogs(true);
    try {
      const response = await getAdminLogs(userId, adminLogsPage, adminLogsPerPage);
      if (response.success && response.data) {
        setAdminLogs(response.data);
        setAdminLogsTotalPages(response.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching admin logs:', error);
    } finally {
      setLoadingAdminLogs(false);
    }
  };

  // Show confirmation modal
  const handleEnrollClick = (courseId: number, courseName: string) => {
    setConfirmationAction({ type: 'enroll', courseId, courseName });
    setShowConfirmModal(true);
  };

  const handleUnenrollClick = (courseId: number, courseName: string) => {
    setConfirmationAction({ type: 'unenroll', courseId, courseName });
    setShowConfirmModal(true);
  };

  // Handle confirmation
  const handleConfirmAction = async () => {
    if (!confirmationAction || !userId) return;

    setIsProcessing(true);
    try {
      if (confirmationAction.type === 'enroll') {
        const response = await enrollUserInCourse(userId, confirmationAction.courseId);
        if (response.success) {
          setShowConfirmModal(false);
          setSuccessMessage({
            title: 'Enrollment Successful',
            message: `${selectedUser?.name} has been successfully enrolled in ${confirmationAction.courseName}.`
          });
          setShowSuccessModal(true);
          fetchCourses();
          fetchLearningHistory();
          fetchAdminLogs();
        } else {
          setShowConfirmModal(false);
          setCoursesError(response.error?.message || 'Failed to enroll user');
        }
      } else {
        const response = await unenrollUserFromCourse(userId, confirmationAction.courseId);
        if (response.success) {
          setShowConfirmModal(false);
          setSuccessMessage({
            title: 'Unenrollment Successful',
            message: `${selectedUser?.name} has been successfully unenrolled from ${confirmationAction.courseName}.`
          });
          setShowSuccessModal(true);
          fetchCourses();
          fetchLearningHistory();
          fetchAdminLogs();
        } else {
          setShowConfirmModal(false);
          setCoursesError(response.error?.message || 'Failed to unenroll user');
        }
      }
    } catch (error) {
      console.error('Error processing enrollment action:', error);
      setShowConfirmModal(false);
      setCoursesError('An unexpected error occurred');
    } finally {
      setIsProcessing(false);
      setConfirmationAction(null);
    }
  };

  // Status change handlers
  const handleChangeStatusClick = () => {
    if (selectedUser) {
      setSelectedStatus(selectedUser.status);
      setShowStatusModal(true);
    }
  };

  const handleStatusSelectionConfirm = async () => {
    if (!userId || !selectedStatus) return;

    setIsChangingStatus(true);
    try {
      const response = await changeUserStatus(userId, selectedStatus);
      if (response.success) {
        setShowStatusModal(false);
        setSuccessMessage({
          title: 'Status Changed Successfully',
          message: `User status has been changed to ${selectedStatus}.`
        });
        setShowSuccessModal(true);
        // Refresh user data
        fetchUserById(userId);
        fetchAdminLogs();
      } else {
        setShowStatusModal(false);
        // Show error via toast or error display
        console.error(response.error?.message);
      }
    } catch (error) {
      console.error('Error changing user status:', error);
      setShowStatusModal(false);
    } finally {
      setIsChangingStatus(false);
    }
  };

  // Role change handlers
  const handleChangeRoleClick = () => {
    if (selectedUser) {
      setSelectedRole(selectedUser.role);
      setShowRoleModal(true);
    }
  };

  const handleRoleSelectionConfirm = async () => {
    if (!userId || !selectedRole) return;

    setIsChangingRole(true);
    try {
      const response = await changeUserRole(userId, selectedRole);
      if (response.success) {
        setShowRoleModal(false);
        setSuccessMessage({
          title: 'Role Changed Successfully',
          message: `User role has been changed to ${selectedRole}.`
        });
        setShowSuccessModal(true);
        // Refresh user data
        fetchUserById(userId);
        fetchAdminLogs();
      } else {
        setShowRoleModal(false);
        console.error(response.error?.message);
      }
    } catch (error) {
      console.error('Error changing user role:', error);
      setShowRoleModal(false);
    } finally {
      setIsChangingRole(false);
    }
  };

  // Password reset handlers
  const handlePasswordResetClick = async () => {
    if (!userId) return;

    setIsResettingPassword(true);
    try {
      const response = await resetUserPassword(userId);
      if (response.success && response.data) {
        setNewPassword(response.data.newPassword);
        setShowPasswordResetModal(true);
        fetchAdminLogs();
      } else {
        console.error(response.error?.message);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/users/list');
  };

  // Fetch courses when search term or page changes
  useEffect(() => {
    if (activeTab === 'enrollment') {
      fetchCourses();
    }
  }, [courseSearchTerm, coursesPage, activeTab]);

  // Fetch admin logs when admin tab is active or page changes
  useEffect(() => {
    if (activeTab === 'admin') {
      fetchAdminLogs();
    }
  }, [adminLogsPage, activeTab]);

  // Reset page when search changes
  useEffect(() => {
    setCoursesPage(1);
  }, [courseSearchTerm]);

  if (loadingUsers || !selectedUser) {
    return (
      <div className="user-details-page-container">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="user-details-page-container">
      <div className="user-details-page-breadcrumb">
        <button onClick={handleBack} className="user-details-back-btn">
          <ArrowLeft size={18} />
        </button>
        <span>User Management</span>
        <span>›</span>
        <span>User Directory</span>
        <span>›</span>
        <span>{selectedUser.name}</span>
      </div>

      <div className="user-details-page-header">
        <div className="user-details-page-info">
          <div className="user-details-avatar-large">{selectedUser.initials}</div>
          <div>
            <h1 className="user-details-page-name">{selectedUser.name}</h1>
            <p className="user-details-page-title">{selectedUser.jobTitle}</p>
          </div>
          <span className={`user-details-status-badge ${selectedUser.status.toLowerCase()}`}>
            {selectedUser.status}
          </span>
        </div>
      </div>

      <div className="user-details-page-tabs">
        <button
          className={`user-details-page-tab ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          User Details
        </button>
        <button
          className={`user-details-page-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Learning History
        </button>
        <button
          className={`user-details-page-tab ${activeTab === 'enrollment' ? 'active' : ''}`}
          onClick={() => setActiveTab('enrollment')}
        >
          Manual Enrollment
        </button>
        <button
          className={`user-details-page-tab ${activeTab === 'admin' ? 'active' : ''}`}
          onClick={() => setActiveTab('admin')}
        >
          Admin Actions
        </button>
      </div>

      <div className="user-details-page-content">
        {activeTab === 'details' && (
          <div className="user-details-tab">
            <section className="user-details-section">
              <h3 className="user-details-section-title">Personal Information</h3>
              <div className="user-details-info-grid">
                <div className="user-details-info-item">
                  <div className="user-details-info-label">EMAIL</div>
                  <div className="user-details-info-value">
                    <Mail size={16} /> {selectedUser.email}
                  </div>
                </div>
                <div className="user-details-info-item">
                  <div className="user-details-info-label">PHONE</div>
                  <div className="user-details-info-value">
                    <Phone size={16} /> {formatFieldValue(selectedUser.phone)}
                  </div>
                </div>
                <div className="user-details-info-item">
                  <div className="user-details-info-label">DEPARTMENT</div>
                  <div className="user-details-info-value">
                    <Users size={16} /> {formatFieldValue(selectedUser.department)}
                  </div>
                </div>
                <div className="user-details-info-item">
                  <div className="user-details-info-label">JOB TITLE</div>
                  <div className="user-details-info-value">
                    <Briefcase size={16} /> {formatFieldValue(selectedUser.jobTitle)}
                  </div>
                </div>
                <div className="user-details-info-item">
                  <div className="user-details-info-label">LOCATION</div>
                  <div className="user-details-info-value">
                    <MapPin size={16} /> {formatFieldValue(selectedUser.location)}
                  </div>
                </div>
                <div className="user-details-info-item">
                  <div className="user-details-info-label">MANAGER</div>
                  <div className="user-details-info-value">
                    <User size={16} /> {formatFieldValue(selectedUser.manager)}
                  </div>
                </div>
                <div className="user-details-info-item">
                  <div className="user-details-info-label">JOIN DATE</div>
                  <div className="user-details-info-value">
                    <Calendar size={16} /> {formatDate(selectedUser.joinDate)}
                  </div>
                </div>
                <div className="user-details-info-item">
                  <div className="user-details-info-label">LAST LOGIN</div>
                  <div className="user-details-info-value">
                    <Clock size={16} /> {formatDate(selectedUser.lastLogin)}
                  </div>
                </div>
              </div>
            </section>

            <section className="user-details-section">
              <h3 className="user-details-section-title">Learning Statistics</h3>
              <div className="user-details-stats-grid">
                <div className="user-details-stat-box">
                  <div className="user-details-stat-box-label">Completed</div>
                  <div className="user-details-stat-box-value">{selectedUser.coursesCompleted || 0}</div>
                  <div className="user-details-stat-box-icon">
                    <CheckCircle size={32} />
                  </div>
                </div>
                <div className="user-details-stat-box">
                  <div className="user-details-stat-box-label">In Progress</div>
                  <div className="user-details-stat-box-value">{selectedUser.coursesActive || 0}</div>
                  <div className="user-details-stat-box-icon">
                    <BookOpen size={32} />
                  </div>
                </div>
                <div className="user-details-stat-box">
                  <div className="user-details-stat-box-label">Completion Rate</div>
                  <div className="user-details-stat-box-value">
                    {selectedUser.totalCourses > 0
                      ? `${Math.round((selectedUser.coursesCompleted / selectedUser.totalCourses) * 100)}%`
                      : '0%'}
                  </div>
                  <div className="user-details-stat-box-icon">
                    <Target size={32} />
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="user-details-history-tab">
            <div className="user-details-history-tab-header">
              <h3 className="user-details-section-title">Learning History</h3>
              <button className="user-details-export-btn">
                <Download size={16} /> Export Transcript
              </button>
            </div>

            <div className="user-details-table-container">
              {loadingHistory ? (
                <div className="user-details-empty-state">
                  <LoadingSpinner size="md" />
                </div>
              ) : learningHistory.length === 0 ? (
                <div className="user-details-empty-state">
                  <BookOpen size={48} />
                  <p>No learning history available</p>
                </div>
              ) : (
                <table className="user-details-table">
                  <thead>
                    <tr>
                      <th>COURSE</th>
                      <th>TYPE</th>
                      <th>STATUS</th>
                      <th>PROGRESS</th>
                      <th>LESSONS</th>
                      <th>ENROLLED DATE</th>
                      <th>COMPLETED DATE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {learningHistory.map(item => (
                      <tr key={item.id}>
                        <td>
                          <div className="user-details-course-info">
                            <div className="user-details-course-icon">
                              <BookOpen size={20} />
                            </div>
                            <span>{item.name}</span>
                          </div>
                        </td>
                        <td>{item.type || '--'}</td>
                        <td>
                          <span className={`user-details-status-badge ${
                            item.status === 'Completed' ? 'completed' :
                            item.status === 'In Progress' ? 'progress' : 'not-started'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                        <td>
                          <div className="user-details-progress-cell">
                            <span>{item.progress}%</span>
                            <div className="user-details-progress-bar">
                              <div
                                className="user-details-progress-fill"
                                style={{ width: `${item.progress}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td>{item.completedLessons}/{item.totalLessons}</td>
                        <td>{formatDate(item.enrolledDate)}</td>
                        <td>{item.completedDate ? formatDate(item.completedDate) : '--'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {activeTab === 'enrollment' && (
          <div className="user-details-enrollment-tab">
            <div className="user-details-enrollment-header">
              <h3 className="user-details-section-title">Manual Enrollment</h3>
              <p className="user-details-enrollment-subtitle">Enroll {selectedUser.name} in courses</p>
            </div>

            <div className="user-details-search-wrapper">
              <Search className="user-details-search-icon" size={18} />
              <input
                type="text"
                className="user-details-search-input"
                placeholder="Search courses..."
                value={courseSearchTerm}
                onChange={(e) => setCourseSearchTerm(e.target.value)}
              />
              {courseSearchTerm && (
                <button
                  className="user-details-search-clear"
                  onClick={() => setCourseSearchTerm('')}
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {coursesError && (
              <ErrorDisplay
                title="Error Loading Courses"
                message={coursesError}
                type="error"
                showRetry
                onRetry={() => fetchCourses()}
                showClose
                onClose={() => setCoursesError(null)}
              />
            )}

            {!coursesError && (
              <div className="user-details-enrollment-list">
                {loadingCourses ? (
                  <div className="user-details-empty-state">
                    <LoadingSpinner size="md" />
                  </div>
                ) : enrollmentCourses.length === 0 ? (
                  <div className="user-details-empty-state">
                    <BookOpen size={48} />
                    <p>No courses found</p>
                  </div>
                ) : (
                  enrollmentCourses.map(course => (
                    <div key={course.id} className="user-details-enrollment-item">
                      <div className="user-details-enrollment-item-info">
                        <div className="user-details-course-icon">
                          <BookOpen size={20} />
                        </div>
                        <div>
                          <div className="user-details-enrollment-item-name">{course.name}</div>
                          <div className="user-details-enrollment-item-meta">
                            {course.lessons} lessons • {course.level}
                          </div>
                        </div>
                      </div>
                      {course.isEnrolled === 1 ? (
                        <button
                          className="user-details-unenroll-btn"
                          onClick={() => handleUnenrollClick(course.id, course.name)}
                        >
                          Unenroll
                        </button>
                      ) : (
                        <button
                          className="user-details-enroll-btn"
                          onClick={() => handleEnrollClick(course.id, course.name)}
                        >
                          + Enroll
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {!coursesError && coursesTotalPages > 1 && (
              <div className="user-details-pagination">
                <button
                  className="user-details-page-btn"
                  onClick={() => setCoursesPage(Math.max(1, coursesPage - 1))}
                  disabled={coursesPage === 1}
                >
                  ‹
                </button>
                {[...Array(Math.min(5, coursesTotalPages))].map((_, i) => {
                  let pageNum = i + 1;
                  if (coursesPage > 3 && coursesTotalPages > 5) {
                    pageNum = coursesPage - 2 + i;
                    if (pageNum > coursesTotalPages) pageNum = coursesTotalPages - (4 - i);
                  }
                  if (pageNum < 1 || pageNum > coursesTotalPages) return null;
                  return (
                    <button
                      key={pageNum}
                      className={`user-details-page-btn ${coursesPage === pageNum ? 'active' : ''}`}
                      onClick={() => setCoursesPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  className="user-details-page-btn"
                  onClick={() => setCoursesPage(Math.min(coursesTotalPages, coursesPage + 1))}
                  disabled={coursesPage === coursesTotalPages}
                >
                  ›
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'admin' && (
          <div className="user-details-admin-tab">
            <div className="user-details-admin-header">
              <h3 className="user-details-section-title">Administrative Actions</h3>
              <p className="user-details-enrollment-subtitle">Perform administrative tasks for this user</p>
            </div>

            <div className="user-details-current-status">
              <div className="user-details-status-label">CURRENT STATUS</div>
              <div className="user-details-status-info">
                <div className="user-details-status-icon">
                  <Shield size={24} />
                </div>
                <div>
                  <span className={`user-details-status-badge ${selectedUser.status.toLowerCase()}`}>
                    {selectedUser.status}
                  </span>
                  <span className="user-details-status-last-login"> • Last login: {formatDate(selectedUser.lastLogin)}</span>
                </div>
              </div>
            </div>

            <div className="user-details-admin-actions-grid">
              <div className="user-details-admin-action-card">
                <div className="user-details-admin-action-icon blue">
                  <Shield size={28} />
                </div>
                <h4 className="user-details-admin-action-title">Update Status</h4>
                <p className="user-details-admin-action-desc">Change account status</p>
                <button className="user-details-admin-action-btn btn-blue" onClick={handleChangeStatusClick}>
                  Change Status
                </button>
              </div>

              <div className="user-details-admin-action-card">
                <div className="user-details-admin-action-icon purple">
                  <Key size={28} />
                </div>
                <h4 className="user-details-admin-action-title">Reset Password</h4>
                <p className="user-details-admin-action-desc">Generate new password</p>
                <button
                  className="user-details-admin-action-btn btn-purple"
                  onClick={handlePasswordResetClick}
                  disabled={isResettingPassword}
                >
                  {isResettingPassword ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>

              <div className="user-details-admin-action-card">
                <div className="user-details-admin-action-icon yellow">
                  <Settings size={28} />
                </div>
                <h4 className="user-details-admin-action-title">Change Role</h4>
                <p className="user-details-admin-action-desc">Update user permissions</p>
                <button
                  className="user-details-admin-action-btn btn-yellow"
                  onClick={handleChangeRoleClick}
                >
                  Change Role
                </button>
              </div>
            </div>

            <div className="user-details-guidelines-box">
              <div className="user-details-guidelines-header">
                <Info size={18} className="user-details-guidelines-icon" />
                <span className="user-details-guidelines-title">Status Change Guidelines</span>
              </div>
              <div className="user-details-guidelines-content">
                <div className="user-details-guideline-item">
                  <strong>Active:</strong> User has full access to the platform
                </div>
                <div className="user-details-guideline-item">
                  <strong>Inactive:</strong> Temporary access suspension (can be reactivated)
                </div>
                <div className="user-details-guideline-item">
                  <strong>Suspended:</strong> Account under review or policy violation
                </div>
              </div>
            </div>

            <div className="user-details-activity-section">
              <h4 className="user-details-activity-title">Recent Admin Activity</h4>
              {loadingAdminLogs ? (
                <div className="user-details-empty-state">
                  <LoadingSpinner size="md" />
                </div>
              ) : adminLogs.length === 0 ? (
                <div className="user-details-empty-state">
                  <Clock size={48} />
                  <p>No admin activity recorded</p>
                </div>
              ) : (
                <>
                  <div className="user-details-activity-list">
                    {adminLogs.map((log) => (
                      <div key={log.id} className="user-details-activity-item">
                        <div className="user-details-activity-icon">
                          <Clock size={16} />
                        </div>
                        <div className="user-details-activity-content">
                          <div className="user-details-activity-action">{log.actionDescription}</div>
                          <div className="user-details-activity-meta">
                            <span className="user-details-activity-admin">By: {log.adminName || 'Unknown Admin'}</span>
                            <span className="user-details-activity-date">{formatDate(log.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {adminLogsTotalPages > 1 && (
                    <div className="user-details-pagination">
                      <button
                        className="user-details-page-btn"
                        onClick={() => setAdminLogsPage(Math.max(1, adminLogsPage - 1))}
                        disabled={adminLogsPage === 1}
                      >
                        ‹
                      </button>
                      {[...Array(Math.min(5, adminLogsTotalPages))].map((_, i) => {
                        let pageNum = i + 1;
                        if (adminLogsPage > 3 && adminLogsTotalPages > 5) {
                          pageNum = adminLogsPage - 2 + i;
                          if (pageNum > adminLogsTotalPages) pageNum = adminLogsTotalPages - (4 - i);
                        }
                        if (pageNum < 1 || pageNum > adminLogsTotalPages) return null;
                        return (
                          <button
                            key={pageNum}
                            className={`user-details-page-btn ${adminLogsPage === pageNum ? 'active' : ''}`}
                            onClick={() => setAdminLogsPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        className="user-details-page-btn"
                        onClick={() => setAdminLogsPage(Math.min(adminLogsTotalPages, adminLogsPage + 1))}
                        disabled={adminLogsPage === adminLogsTotalPages}
                      >
                        ›
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setConfirmationAction(null);
        }}
        onConfirm={handleConfirmAction}
        title={confirmationAction?.type === 'enroll' ? 'Confirm Enrollment' : 'Confirm Unenrollment'}
        message={
          confirmationAction?.type === 'enroll'
            ? `Are you sure you want to enroll ${selectedUser?.name} in "${confirmationAction.courseName}"?`
            : `Are you sure you want to unenroll ${selectedUser?.name} from "${confirmationAction?.courseName}"? This will remove all progress data.`
        }
        confirmText={confirmationAction?.type === 'enroll' ? 'Enroll' : 'Unenroll'}
        cancelText="Cancel"
        confirmButtonStyle={confirmationAction?.type === 'enroll' ? 'success' : 'danger'}
        isLoading={isProcessing}
      />

      {/* Success Modal */}
      <MessageModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        type="success"
        title={successMessage.title}
        message={successMessage.message}
      />

      {/* Status Selection Modal */}
      {showStatusModal && (
        <div className="confirmation-modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="confirmation-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="confirmation-modal-title">Change User Status</h2>
            <p className="confirmation-modal-message">
              Select the new status for {selectedUser?.name}:
            </p>

            <div className="status-select-container">
              <select
                className="status-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value as UserStatus)}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Suspended">Suspended</option>
              </select>
            </div>

            <div className="confirmation-modal-actions">
              <button
                className="confirmation-modal-button confirmation-modal-button-cancel"
                onClick={() => setShowStatusModal(false)}
                disabled={isChangingStatus}
              >
                Cancel
              </button>
              <button
                className="confirmation-modal-button confirmation-modal-button-primary"
                onClick={handleStatusSelectionConfirm}
                disabled={isChangingStatus}
              >
                {isChangingStatus ? (
                  <span>
                    <span className="confirmation-modal-spinner"></span>
                    Changing...
                  </span>
                ) : (
                  'Continue'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Change Modal */}
      {showRoleModal && (
        <div className="confirmation-modal-overlay" onClick={() => setShowRoleModal(false)}>
          <div className="confirmation-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="confirmation-modal-title">Change User Role</h2>
            <p className="confirmation-modal-message">
              Select the new role for {selectedUser?.name}:
            </p>

            <div className="status-select-container">
              <select
                className="status-select"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="student">Student</option>
                <option value="instructor">Instructor</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>

            <div className="confirmation-modal-actions">
              <button
                className="confirmation-modal-button confirmation-modal-button-cancel"
                onClick={() => setShowRoleModal(false)}
                disabled={isChangingRole}
              >
                Cancel
              </button>
              <button
                className="confirmation-modal-button confirmation-modal-button-primary"
                onClick={handleRoleSelectionConfirm}
                disabled={isChangingRole}
              >
                {isChangingRole ? (
                  <span>
                    <span className="confirmation-modal-spinner"></span>
                    Changing...
                  </span>
                ) : (
                  'Continue'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Result Modal */}
      {showPasswordResetModal && (
        <div className="confirmation-modal-overlay" onClick={() => setShowPasswordResetModal(false)}>
          <div className="confirmation-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="confirmation-modal-title">Password Reset Successful</h2>
            <p className="confirmation-modal-message">
              A new password has been generated for {selectedUser?.name}.
            </p>

            <div className="password-display-container">
              <label className="password-display-label">New Password:</label>
              <div className="password-display-box">
                <code className="password-display-code">{newPassword}</code>
                <button
                  className="password-copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(newPassword);
                    setSuccessMessage({
                      title: 'Copied!',
                      message: 'Password copied to clipboard'
                    });
                    setShowPasswordResetModal(false);
                    setShowSuccessModal(true);
                  }}
                >
                  Copy
                </button>
              </div>
              <p className="password-display-note">
                Please share this password with the user securely. It will not be shown again.
              </p>
            </div>

            <div className="confirmation-modal-actions">
              <button
                className="confirmation-modal-button confirmation-modal-button-primary"
                onClick={() => setShowPasswordResetModal(false)}
                style={{ width: '100%' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetailsPage;
